import { useRef, useState } from "react";
import { Send, Loader, CheckCircle, XCircle, Clock, CheckCheck, Eye, AlertTriangle, X, Reply } from "lucide-react";
import { StatRow } from "./BroadcastAtoms";
import TemplatePreview from "./TemplatePreview";
import { pct, STATUS_LABEL, STATUS_BADGE_CLASS } from "./constants";
import { useMessageSendingProgress } from "../../hooks/useMessageSendingProgress";
import BroadcastContactsSidebar from "./BroadcastContactsSidebar";
import api from "../../services/api";

const FailuresModal = ({ broadcastId, onClose }) => {
  const [failures, setFailures] = useState(null);
  const [loading, setLoading]   = useState(true);

  useState(() => {
    api.get(`/broadcasts/${broadcastId}/failures`)
      .then((r) => setFailures(r.data.data || []))
      .catch(() => setFailures([]))
      .finally(() => setLoading(false));
  }, [broadcastId]);

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[70vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-red-400" />
            <p className="text-sm font-semibold text-gray-800">Failed Recipients</p>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg transition-colors">
            <X className="w-4 h-4 text-gray-500" />
          </button>
        </div>
        <div className="overflow-y-auto flex-1 px-4 py-3 space-y-2">
          {loading && <p className="text-sm text-gray-400 text-center py-4">Loading…</p>}
          {!loading && failures?.length === 0 && (
            <p className="text-sm text-gray-400 text-center py-4">No failure details available.</p>
          )}
          {!loading && failures?.length > 0 && (
            <p className="text-[11px] text-gray-400 mb-1">{failures.length} recipient{failures.length > 1 ? "s" : ""} failed</p>
          )}
          {!loading && failures?.map((f, i) => {
            const name  = f.Customer?.name || "Unknown contact";
            const phone = f.Customer?.phoneNumber || "—";
            return (
              <div key={f.id} className="bg-red-50 border border-red-100 rounded-xl px-3 py-2.5">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-[13px] font-semibold text-gray-800 truncate">{i + 1}. {name}</p>
                    <p className="text-[11px] text-gray-500 font-mono">{phone}</p>
                  </div>
                  <span className="text-[10px] text-red-400 bg-red-100 px-1.5 py-0.5 rounded-full flex-shrink-0 mt-0.5">failed</span>
                </div>
                {f.error && (
                  <p className="text-[11px] text-red-500 mt-1.5 leading-relaxed border-t border-red-100 pt-1.5">
                    {f.error}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const STATUS_ICON = {
  completed: <CheckCircle className="w-3.5 h-3.5 text-green-500" />,
  sending:   <Loader className="w-3.5 h-3.5 text-blue-500 animate-spin" />,
  failed:    <XCircle className="w-3.5 h-3.5 text-red-400" />,
  pending:   <Clock className="w-3.5 h-3.5 text-yellow-500" />,
};

const FINAL = new Set(["completed", "failed"]);

const LiveBroadcast = ({ b }) => {
  const [showFailures, setShowFailures] = useState(false);
  const [contactsSidebar, setContactsSidebar] = useState(null); // { status }

  const openSidebar = (status) => setContactsSidebar({ status });

  // Always poll if there's a jobId and the DB status isn't final yet.
  // Once progress reaches a terminal state we latch it so the bubble
  // doesn't revert to a stale DB status while the next poll is in-flight.
  const latchedStatus = useRef(null);

  const needsProgress = b.jobId && !FINAL.has(b.status);
  const { progress } = useMessageSendingProgress(needsProgress ? b.jobId : null);

  // Latch completed/failed so the bubble never flickers back to "sending"
  if (progress?.status && FINAL.has(progress.status)) {
    latchedStatus.current = progress.status;
  }

  const status = latchedStatus.current ?? progress?.status ?? b.status;

  const sent    = progress?.sentMessages  ?? b.sentMessages    ?? 0;
  const total   = progress?.totalMessages ?? b.totalMessages   ?? 0;
  const unread  = b.deliveredMessages ?? 0;
  const read    = b.readMessages      ?? 0;
  const replied = b.repliedMessages   ?? 0;
  const failed  = progress?.failedMessages ?? b.failedMessages ?? 0;

  const sentPct    = pct(sent, total);
  const unreadPct  = pct(unread, sent);
  const readPct    = pct(read, sent);
  const repliedPct = pct(replied, sent);
  const failedPct  = pct(failed, total);

  const date = new Date(b.createdAt).toLocaleString(undefined, {
    month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
  });

  return (
    <>
    <div className="flex justify-end mb-3 px-3">
      <div className="bg-[#d9fdd3] rounded-2xl rounded-tr-none shadow-md w-full max-w-[340px] border border-[#c6f0c2]">

        {/* Header */}
        <div className="flex items-start justify-between gap-2 px-3 pt-2.5 pb-1.5 border-b border-[#c6f0c2]">
          <div className="min-w-0">
            <p className="text-[13px] font-bold text-gray-800 truncate">
              {b.name || b.template?.templateName || "—"}
            </p>
            {b.name && (
              <p className="text-[11px] text-gray-500 truncate">{b.template?.templateName}</p>
            )}
          </div>
          <div className="flex items-center gap-1 flex-shrink-0 mt-0.5">
            {STATUS_ICON[status] || STATUS_ICON.pending}
          </div>
        </div>

        {/* Template preview */}
        <TemplatePreview b={b} />

        {/* Send progress bar */}
        {status === "sending" && (
          <div className="px-3 pb-2">
            <div className="flex items-center gap-2 mb-0.5">
              <div className="flex-1 bg-white/60 rounded-full h-1.5 overflow-hidden">
                <div className="h-full bg-blue-400 rounded-full transition-all duration-500" style={{ width: `${sentPct}%` }} />
              </div>
              <span className="text-[11px] text-gray-600 whitespace-nowrap">{sent}/{total}</span>
            </div>
          </div>
        )}

        {/* Delivery stats */}
        <div className="px-3 pb-2 space-y-1.5">
          <StatRow
            icon={<Send className="w-3 h-3 text-gray-400" />}
            bgColor="bg-white/50" barColor="bg-gray-400"
            value={`${sent}/${total} (${sentPct}%)`} pctVal={sentPct} label="text-gray-500"
            onClick={() => openSidebar("sent")}
          />
          {(unread > 0 || status === "completed") && (
            <StatRow
              icon={<CheckCheck className="w-3 h-3 text-orange-400" />}
              bgColor="bg-orange-50" barColor="bg-orange-300"
              value={`${unread} (${unreadPct}%)`} pctVal={unreadPct} label="text-orange-500"
              onClick={() => openSidebar("delivered")}
            />
          )}
          {(read > 0 || status === "completed") && (
            <StatRow
              icon={<Eye className="w-3 h-3 text-purple-500" />}
              bgColor="bg-purple-100" barColor="bg-purple-400"
              value={`${read} (${readPct}%)`} pctVal={readPct} label="text-purple-600"
              onClick={() => openSidebar("read")}
            />
          )}
          {(replied > 0 || status === "completed") && (
            <StatRow
              icon={<Reply className="w-3 h-3 text-teal-500" />}
              bgColor="bg-teal-50" barColor="bg-teal-400"
              value={`${replied} (${repliedPct}%)`} pctVal={repliedPct} label="text-teal-600"
              onClick={() => openSidebar("replied")}
            />
          )}
          {failed > 0 && (
            <StatRow
              icon={<XCircle className="w-3 h-3 text-red-400" />}
              bgColor="bg-red-100" barColor="bg-red-400"
              value={`${failed} (${failedPct}%)`} pctVal={failedPct} label="text-red-500"
              onClick={() => openSidebar("failed")}
            />
          )}
        </div>

        {/* Footer */}
        <div className="px-3 pb-2 pt-1 flex items-center justify-between">
          <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${STATUS_BADGE_CLASS[status] || STATUS_BADGE_CLASS.pending}`}>
            {STATUS_LABEL[status] || "Pending"}
          </span>
          <div className="flex items-center gap-1.5">
            {failed > 0 && (
              <button
                onClick={() => setShowFailures(true)}
                className="flex items-center gap-0.5 text-[10px] text-red-400 hover:text-red-500 transition-colors"
              >
                <AlertTriangle className="w-3 h-3" />
                {failed} failed
              </button>
            )}
            <span className="text-[10px] text-gray-400">{date}</span>
            {status === "completed" && <CheckCheck className="w-3 h-3 text-blue-500" />}
          </div>
        </div>

      </div>
    </div>
    {showFailures && <FailuresModal broadcastId={b.id} onClose={() => setShowFailures(false)} />}
    {contactsSidebar && (
      <BroadcastContactsSidebar
        broadcastId={b.id}
        initialStatus={contactsSidebar.status}
        onClose={() => setContactsSidebar(null)}
      />
    )}
    </>
  );
};

export default LiveBroadcast;
